import { Fragment, useRef, useState, useMemo } from 'react';

import { GooSpinner } from 'react-spinners-kit';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

import { generateArray } from '@utils';
import { useNewsFeedQuery, useIntersectionObserver } from '@hooks';

import FeedFilters from './FeedFilters';
import Post from './Post';
import PostLoading from './Post/Loading';
import * as S from './styles';
import NewPost from './NewPost';

const Feed = () => {
  const theme = useTheme();
  const [activeFilter, setActiveFilter] = useState('all');

  const newsfeedQuery = useNewsFeedQuery(true, activeFilter);
  const { t } = useTranslation();

  const loadMoreButtonRef = useRef<HTMLButtonElement>(null);

  useIntersectionObserver({
    target: loadMoreButtonRef,
    onIntersect: newsfeedQuery.fetchNextPage,
    enabled: newsfeedQuery.hasNextPage,
  });

  const isEmpty = useMemo(() => {
    if (newsfeedQuery.isLoading) return false;
    if (!newsfeedQuery.data?.pages) return true;
    return newsfeedQuery.data.pages.every((p) => p.data.length === 0);
  }, [newsfeedQuery.isLoading, newsfeedQuery.data?.pages]);

  return (
    <S.Container>
      <NewPost />

      <FeedFilters
        activeFilter={activeFilter}
        onFilterChange={setActiveFilter}
      />

      {newsfeedQuery.isLoading &&
        generateArray(3).map((key) => (
          <PostLoading key={`newsfeed-post-${key}`} />
        ))}

      {!newsfeedQuery.isLoading && isEmpty && (
        <S.EmptyState>
          <svg
            width="120"
            height="120"
            viewBox="0 0 120 120"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="60" cy="60" r="50" fill="#F1F5F9" />
            <rect x="38" y="35" width="44" height="6" rx="3" fill="#CBD5E1" />
            <rect x="38" y="47" width="32" height="6" rx="3" fill="#E2E8F0" />
            <rect x="38" y="59" width="44" height="6" rx="3" fill="#CBD5E1" />
            <rect x="38" y="71" width="24" height="6" rx="3" fill="#E2E8F0" />
          </svg>
          <h3>Nenhuma publicação encontrada</h3>
          <p>Não há publicações para este filtro no momento.</p>
        </S.EmptyState>
      )}

      {!newsfeedQuery.isLoading &&
        !isEmpty &&
        newsfeedQuery.data?.pages.map(({ meta, data }) => {
          const key = `current-page-${meta.pagination.current_page}`;

          return (
            <Fragment key={key}>
              {data.map((post) => (
                <Post key={`newsfeed-post-${post.id}`} data={post} />
              ))}
            </Fragment>
          );
        })}

      {newsfeedQuery.isFetchingNextPage ? (
        <S.SpinnerWrapper>
          <GooSpinner
            size={2.5}
            sizeUnit="rem"
            color={theme.colors.brand.primary.default}
          />
        </S.SpinnerWrapper>
      ) : (
        newsfeedQuery.hasNextPage && (
          <S.LoadMoreButton
            type="button"
            ref={loadMoreButtonRef}
            onClick={() => newsfeedQuery.fetchNextPage()}
          >
            {t('pages.newsfeed.load')}
          </S.LoadMoreButton>
        )
      )}
    </S.Container>
  );
};

export default Feed;
